import { Injectable } from '@angular/core';
import { StarbaseDataServiceWithRequisites } from 'starbase-launcher';
import { LU_Keyword, KeywordLinking } from '../models/keyword';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { WebApiService } from 'starbase-login';

@Injectable({
  providedIn: 'root'
})
export class KeywordDataService extends StarbaseDataServiceWithRequisites<LU_Keyword> {
  protected _staleDataTimeout = 2000;
  protected _getOneUri = '/api/LU_KeywordModel/';
  protected _getAllUri = '/api/LU_KeywordModel';
  protected _postUri = '/api/LU_KeywordModel';
  protected _putUri = '/api/LU_KeywordModel/';
  protected _deleteUri = '/api/LU_KeywordModel/';
  protected _broadSearchUri = '/api/LU_KeywordModel/GetKeywordsBroadSearch';
  private _getByTextUri = '/api/LU_KeywordModel/GetLU_KeywordByText?keywordText=';
  private _mergeKeywordsUri = '/api/LU_KeywordModel/MergeKeywords';
  private _postKeywordLinkingUri = '/api/KeywordLinkingModels';
  private _deleteKeywordLinkingUri = '/api/KeywordLinkingModels/';
  private _postKeywordSynonymsUri = '/api/LU_KeywordModel/Synonyms/';
  private _postKeywordChildrenUri = '/api/LU_KeywordModel/Children/';

  private _selectedKeyword: LU_Keyword;
  private _synonymizationModal = false;

  // A cached record which contains the latest keywords from the LU_Keywords table. Includes their children and synonyms
  private _keywordsMap: Map<number, LU_Keyword> = new Map();

  constructor(private _http: HttpClient, private _webApiSvc: WebApiService) {
    super(_http, _webApiSvc);
  }
  public get getByTextUri() {
    return this._getByTextUri;
  }

  public get synonymizationModalVisable() {
    return this._synonymizationModal;
  }

  public get mergeKeywordsUri() {
    return this._mergeKeywordsUri;
  }

  public get selectedKeyword() {
    return this._selectedKeyword;
  }

  public getOneKeyword(keywordUID: number): Observable<LU_Keyword>{
    return this.getRequisiteData(this._getOneUri + keywordUID);
  }

  public showSynonymizationModal(keyword: LU_Keyword) {
    this._selectedKeyword = keyword;
    this._synonymizationModal = true;
  }

  public getAllSynonymAndChildrenData() {
    console.log(`Fetching All Synonyms & Children`);
    this.forceStaleData();
    return new Promise<LU_Keyword[]>((res, rej) => {
      this.getAllData().subscribe(keywords => {
        for (const keyword of keywords) {
          this._keywordsMap.set(keyword.uid, keyword);
        }

        res(keywords);
      }, err => {
        console.error(err);
        rej(err);
      });
    });
  }

  // Will recursively get all children UIDs. Accepts either a LU_Keyword or number coresponding to its UID
  public getAllChildrenUIDs(keyword: LU_Keyword | number): number[] {
    const children: number[] = [];
    const uid = (typeof keyword === 'number')? keyword : keyword.uid;

    for (const child of (this._keywordsMap.get(uid) as LU_Keyword)?.children) {
      children.push(child.uid);
      children.push(...this.getAllChildrenUIDs(child));
    }

    return [...new Set(children)];
  }

  public hideSynonymizationModal() {
    this._synonymizationModal = false;
  }

  public mergeKeywords(target: LU_Keyword, toMerge: LU_Keyword[], markReviewed: boolean): Observable<any> {
    if (!target || !toMerge || !toMerge.length) {
      throw new Error('Invalid call to mergeKeywords().');
    }
    const mergingKeywords = toMerge.map(k => k.uid);
    return this.postRequisiteData<{ item1: number; item2: number[]; item3: boolean }>(this.mergeKeywordsUri,
      { item1: target.uid, item2: mergingKeywords, item3: markReviewed });
  }

  public addKeywordLink(link: KeywordLinking): Observable<KeywordLinking> {
    return this.postRequisiteData<KeywordLinking>(this._postKeywordLinkingUri, link);
  }

  public removeKeywordLink(link: KeywordLinking): Observable<any> {
    return this.deleteData(this._deleteKeywordLinkingUri, link.uid);
  }

  public getKeywordChildren(uid: number) {
    return this._keywordsMap.get(uid)?.children || [];
  }

  public getKeywordParents(uid: number) {
    return this._keywordsMap.get(uid)?.parents || [];
  }

  public getKeywordSynonyms(uid: number) {
    return this._keywordsMap.get(uid)?.synonyms || [];
  }

  public getKeyword(uid: number) {
    return this._keywordsMap.get(uid);
   }

  public updateKeywordSynonymization(setForRemoval: number[], setForAddition: number[], keywordUID: number, mode: 'synonyms'| 'hierarchy'){
    const url = (mode === 'synonyms'? this._postKeywordSynonymsUri : this._postKeywordChildrenUri) + keywordUID;
    return new Promise<LU_Keyword>((res, rej) => {
      this.postRequisiteData<{ item1: number[]; item2: number[] }>(url, {
        item1: setForRemoval,
        item2: setForAddition,
      }).subscribe(result => {
        this._keywordsMap.set(keywordUID, result);
        res(result);
      }, error => rej(error));
    });
  }

  // Checks whether the keyword supplied is a synonym of another.
  public isSynonymOfKeywordText(keyword: LU_Keyword, checkTxt: string): boolean {
    for (const synonym of this.getKeywordSynonyms(keyword.uid)) {
      if (synonym.keyword === checkTxt) {
        return true;
      }
    }

    return false;
  }

  public isDependentOfKeywordText(kw: LU_Keyword, checkTxt: string, checked: LU_Keyword[] = []): boolean {
    const parents = this._keywordsMap.get(kw.uid)?.parents || [];

    if (checked.some(ckw => ckw.uid == kw.uid)) {
      return false;
    } else {
      checked.push(kw);
    }

    for (const parent of parents) {
      if (parent.keyword === checkTxt) {
        return true;
      }
      const result = this.isDependentOfKeywordText(parent, checkTxt, checked);
      if (result) {
        return true;
      }
    }

    return false;
  }

}
